import {
  BrandEntry,
  BreadcrumbItem,
  ComparisonEntry,
  DoctorEntry,
  MoleculeEntry,
  RegistryEntry,
  ThemeEntry
} from '../types'
import { BRANDS } from './brands'
import { COMPARISONS } from './comparisons'
import { DOCTORS, MEDICAL_TEAM_PATH } from './doctors'
import { MOLECULES } from './molecules'
import { HOME_PATH, STATIC_PAGES } from './pages'
import { GLP1_HUB_SLUG, OBSERVATORY_ARCHIVES, THEMES } from './themes'

export { BRANDS } from './brands'
export { buildComparison, COMPARISON_REDIRECTS, COMPARISON_SLUGS, COMPARISONS } from './comparisons'
export { DOCTORS, MEDICAL_TEAM_PATH } from './doctors'
export { MOLECULES } from './molecules'
export { HOME_PATH, STATIC_PAGES } from './pages'
export { GLP1_HUB_SLUG, THEMES } from './themes'

/** Every known organic entry, published or not. */
export const ALL_ENTRIES: RegistryEntry[] = [
  ...STATIC_PAGES,
  ...THEMES,
  ...OBSERVATORY_ARCHIVES,
  ...MOLECULES,
  ...BRANDS,
  ...COMPARISONS,
  ...DOCTORS
]

export const getBrand = (slug: string): BrandEntry | undefined => BRANDS.find((entry) => entry.slug === slug)
export const getMolecule = (slug: string): MoleculeEntry | undefined => MOLECULES.find((entry) => entry.slug === slug)
export const getComparison = (slug: string): ComparisonEntry | undefined =>
  COMPARISONS.find((entry) => entry.slug === slug)
export const getTheme = (slug: string): ThemeEntry | undefined => THEMES.find((entry) => entry.slug === slug)
export const getDoctor = (slug: string): DoctorEntry | undefined => DOCTORS.find((entry) => entry.slug === slug)

/** Brand → molecule (PRD §30: Mounjaro → tirzepatida, the others → semaglutida). */
export const getMoleculeForBrand = (brandSlug: string): MoleculeEntry | undefined => {
  const brand = getBrand(brandSlug)
  return brand ? getMolecule(brand.activeIngredient) : undefined
}

/** Molecule → brands (PRD §28 `availableBrands`, PRD §31), in registry order. */
export const getBrandsByMolecule = (moleculeSlug: string): BrandEntry[] =>
  BRANDS.filter((brand) => brand.activeIngredient === moleculeSlug)

/** Comparisons that involve a given brand or molecule (PRD §25 `comparisons`). */
export const getComparisonsFor = (slug: string): ComparisonEntry[] =>
  COMPARISONS.filter((comparison) => comparison.left.slug === slug || comparison.right.slug === slug)

const normalizePath = (path: string): string => {
  const clean = path.split(/[?#]/)[0].replace(/\/+$/, '')
  return clean === '' ? '/' : clean
}

export const getEntryByPath = (path: string): RegistryEntry | undefined => {
  const target = normalizePath(path)
  return ALL_ENTRIES.find((entry) => entry.path === target)
}

const crumb = (entry: RegistryEntry | undefined): BreadcrumbItem[] =>
  entry ? [{ name: entry.name, path: entry.path }] : []

/**
 * Breadcrumb trail for a registry entry (PRD §55), e.g. Medleve > GLP-1 > Semaglutida > Ozivy.
 * Returns `[]` for the homepage. Paths are raw: `resolveBreadcrumbs` decides which ones become links.
 */
export const getBreadcrumbTrail = (entry: RegistryEntry): BreadcrumbItem[] => {
  const home = STATIC_PAGES.find((page) => page.path === HOME_PATH)
  const root = crumb(home)
  const hub = crumb(getTheme(GLP1_HUB_SLUG))
  const self = crumb(entry)

  switch (entry.kind) {
    case 'page':
      return entry.path === HOME_PATH ? [] : [...root, ...self]
    case 'theme':
      return [...root, ...self]
    case 'molecule':
    case 'comparison':
      return [...root, ...hub, ...self]
    case 'brand':
      return [...root, ...hub, ...crumb(getMolecule(entry.activeIngredient)), ...self]
    case 'doctor':
      return [...root, ...crumb(STATIC_PAGES.find((page) => page.path === MEDICAL_TEAM_PATH)), ...self]
  }
}
